import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [city, setCity] = useState(localStorage.getItem("bhega_city") || "");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("bhega_token");
    const stored = localStorage.getItem("bhega_user");
    if (token && stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
        // Refresh from server (city may have changed elsewhere).
        api.get("/auth/me").then((r) => {
          const fresh = r.data?.user;
          if (fresh) {
            setUser(fresh);
            localStorage.setItem("bhega_user", JSON.stringify(fresh));
            if (fresh.city) {
              setCity(fresh.city);
              localStorage.setItem("bhega_city", fresh.city);
            }
          }
        }).catch(() => { /* token may be stale — the global 401 interceptor will handle logout */ });
      } catch (e) { /* noop */ }
    }
    setReady(true);
  }, []);

  const login = (token, userObj) => {
    localStorage.setItem("bhega_token", token);
    localStorage.setItem("bhega_user", JSON.stringify(userObj));
    setUser(userObj);
    // If the returning user already has a city on the server, restore it.
    if (userObj?.city) {
      setCity(userObj.city);
      localStorage.setItem("bhega_city", userObj.city);
    }
  };

  const logout = () => {
    localStorage.removeItem("bhega_token");
    localStorage.removeItem("bhega_user");
    setUser(null);
  };

  // Global session-expiry handler — fires when the api layer catches a 401
  // on a tokened request. Clears in-memory state + redirects to Home.
  useEffect(() => {
    const onExpire = () => {
      // Avoid toasting / redirecting if the user is already signed out
      // (e.g. the token had already been removed by `logout()`).
      const stillAuthed = !!localStorage.getItem("bhega_user") || !!user;
      logout();
      toast.message("Your session has expired", {
        description: "Please sign in again to continue.",
        duration: 5000,
      });
      // Hard-redirect to the home page so protected views unmount cleanly.
      if (stillAuthed && typeof window !== "undefined") {
        // Keep users on the About / How-it-works marketing routes if they're
        // already there — otherwise bounce to the home page.
        const path = window.location.pathname;
        const safePublic = ["/", "/about", "/about-us", "/how-it-works", "/browse"].some(
          (p) => path === p || path.startsWith(`${p}/`),
        );
        if (!safePublic) {
          window.location.assign("/");
        }
      }
    };
    window.addEventListener("bhega:session-expired", onExpire);
    return () => window.removeEventListener("bhega:session-expired", onExpire);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCity = (c) => {
    setCity(c);
    if (c) localStorage.setItem("bhega_city", c);
    else localStorage.removeItem("bhega_city");
    // Persist on the user's account too — so re-logins remember it.
    if (user) {
      api.patch("/me/city", { city: c || "" }).catch(() => { /* best effort */ });
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, city, setCity: updateCity, ready, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
